/**
 * DrawingPhaseShell: M4 batch 6b.
 *
 * Composition for `phase === "drawing"`:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │  Round X • Turn Y/Z   ⏱ Ns                                  │
 *   │  Drawer: {drawerName}                                       │
 *   │  Word:   {full word | skeleton}                             │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │           <DrawingCanvas readOnly={!isDrawer} />            │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │  {drawer ? <Toolbar /> : <GuessInput />}                    │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Word display logic (per PRD §3.4 + §3.5):
 *  - drawer            → full word verbatim
 *  - audience-correct  → full word (already guessed, no leak)
 *  - audience-pending  → skeleton + revealedIndices from the hint scheduler
 *  - spectator         → skeleton with no reveals
 *
 * Timer: reads `msRemaining` from RoomShell's `ConnState.msRemaining`
 * (originally from the server snapshot) and counts down locally via a
 * 250ms setInterval. Cosmetic only, server is authoritative for the
 * actual phase end via PHASE_END(draw-timer-expired).
 *
 * Drawer derivation uses `currentTurn.drawerId` directly (per batch-6a
 * finding): no recomputation from turnOrder.
 *
 * Batch 6b does NOT include: GuessInput hook-up (commit 3 wires it),
 * reveal screen, scoreboard, REMATCH button, all deferred to 6c.
 */

import { DrawingCanvas } from "../canvas/DrawingCanvas.js";
import { CursorOverlay } from "../canvas/CursorOverlay.js";
import { Toolbar } from "../tools/Toolbar.js";
import { WordSkeleton } from "../components/WordSkeleton.js";
import { DrawingPhaseTimer } from "../components/DrawingPhaseTimer.js";
import { PlayersPanel } from "../components/PlayersPanel.js";
import { GuessChatFeed } from "../components/GuessChatFeed.js";
import { useEffect, useRef } from "preact/hooks";
import { useUndoClear } from "../canvas/useUndoClear.js";
import { clearStrokes, lastStrokeId, popStrokeById } from "../canvas/strokesStore.js";
import { useDeadlineCountdown } from "../hooks/useDeadlineCountdown.js";
import { buildTurnScores } from "../game/turnScores.js";
import type { ClientControlFrame, GameStateView } from "../ws.js";
import type { SerializedPlayer } from "@drawlol/shared";
import { PAGE_TEAR_IN_CLASS } from "../anim/page-tear.js";
import { InkCat } from "../marks/v1/InkCat.js";

/**
 * V4.2.T8 (note 5b): the header leads with `Turn N/M`. The `Round` token is
 * HIDDEN in the common single-round case (`totalRounds === 1`) and only shown
 * as `Round r/R` for multi-round games (R > 1), so a one-round game reads
 * "Turn 1/4" with no redundant "Round 1/1" noise.
 */
function headerLabel(
  turnIndex: number,
  totalTurns: number,
  currentRound: number,
  totalRounds: number,
): string {
  const turn = `Turn ${turnIndex + 1}/${totalTurns}`;
  if (totalRounds <= 1) return turn;
  return `${turn} • Round ${currentRound}/${totalRounds}`;
}

type DrawingPhaseShellProps = {
  /** Local player's id: used to compute drawer/audience role. */
  playerId: string;
  /** Cached game-state projection from RoomShell. */
  game: GameStateView;
  /**
   * Player roster: used to look up the drawer's display name AND to render the
   * left <PlayersPanel> (which needs each player's avatar `color`). Carries the
   * full `SerializedPlayer` (id + name + color).
   */
  players: ReadonlyArray<SerializedPlayer>;
  /**
   * Room code: drives the persistent in-game invite chip (<ShareLinkCTA>) at
   * the bottom of the left players panel (V4.2.T9, funnel).
   */
  roomId: string;
  /**
   * Whether the local player is in the spectators set (joined mid-game).
   * Spectators see the skeleton with no reveals + no guess input.
   */
  isSpectator: boolean;
  /** Wall-clock ms remaining in the drawing phase (from snapshot). */
  msRemaining: number;
  /**
   * v4 T7: the drawing phase's total duration in seconds (`drawTimerSeconds`
   * from settings). The timer divides `secondsLeft` by this to pick its
   * urgency stop. Omitted by legacy call sites / pre-settings snapshots →
   * `timerStop` falls back to the calm stop (no false red alarm).
   */
  phaseTotalSeconds?: number;
  /**
   * The audience guess input (an <AudienceGuessSheet>). Passed as a render
   * prop so tests can stub it. V4.2.T10a: on DESKTOP this renders in the RIGHT
   * column beneath the wrong-guess feed; on mobile portrait the sheet
   * positions itself fixed-to-bottom regardless of where it's mounted.
   */
  guessInputSlot?: preact.ComponentChildren;
  /**
   * M4 batch 7 commit 2: binary STROKE_* sender. Only the drawer uses it;
   * audience + spectator pass-through is a no-op since their canvas is
   * `readOnly`. Optional so unit tests can mount without a WS layer.
   */
  sendBinary?: (buf: ArrayBuffer) => boolean;
  /**
   * M4 batch 7 commit 4: JSON control frame sender, used to dispatch
   * STROKE_UNDO + STROKE_CLEAR when the drawer triggers them via the
   * Toolbar. Only the drawer wires it; audience + spectator skip the
   * toolbar entirely.
   */
  sendFrame?: (frame: ClientControlFrame) => boolean;
  /**
   * Protocol 8 (F1): id → name memory for players no longer in the roster.
   * The disconnected drawer is PRUNED from `players` the moment their
   * socket closes (PLAYER_LEFT_BROADCAST), without this fallback the
   * header read "Drawer: The drawer" for the whole 10s grace window.
   */
  departedNames?: ReadonlyMap<string, string>;
};

export function DrawingPhaseShell({
  playerId,
  game,
  players,
  roomId,
  isSpectator,
  msRemaining,
  phaseTotalSeconds,
  guessInputSlot,
  sendBinary,
  sendFrame,
  departedNames,
}: DrawingPhaseShellProps) {
  const turn = game.currentTurn;
  const drawerId = turn?.drawerId ?? "";
  const drawerName =
    players.find((p) => p.id === drawerId)?.name ?? departedNames?.get(drawerId) ?? "The drawer";
  const isDrawer = drawerId === playerId;
  // Running per-player score from completed turns (shared scoreboard helper).
  const scores = buildTurnScores(players, game.turnSummaries);
  // audience-correct: local player already submitted a correct guess this turn.
  const isAudienceCorrect = !!turn?.correctGuesserIds.includes(playerId);
  // audience-pending: not drawer, not spectator, not correct yet.
  const showFullWord = isDrawer || isAudienceCorrect;

  // Local countdown: purely cosmetic; server PHASE_END is authoritative.
  const secondsLeft = useDeadlineCountdown(msRemaining);

  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  // Task 6 (bug C): entering the drawing phase after interacting with lower-page
  // lobby/word-pick controls could leave the page pre-scrolled with the canvas
  // top clipped. Reset to the top on mount (the shell remounts on every phase
  // swap). Guarded on scrollY so it's a no-op when already at the top, which
  // also keeps it silent under jsdom, which has no real scroll to reset.
  useEffect(() => {
    if (typeof window !== "undefined" && window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
  }, []);

  // M4 batch 7 commit 4: Toolbar undo/clear → strokes store + server.
  // Hook is always called (rules of hooks); the resulting handlers are
  // only attached to the Toolbar when isDrawer below. Optimistic local
  // mutation first, then a STROKE_UNDO / STROKE_CLEAR JSON frame goes
  // out: the broadcast back is a dedup no-op on the local store.
  const { clearHolding, onClearStart, onClearEnd, onUndo } = useUndoClear({
    onUndo: () => {
      const id = lastStrokeId();
      if (!id) return;
      popStrokeById(id);
      sendFrame?.({ kind: "STROKE_UNDO" });
    },
    onClear: () => {
      clearStrokes();
      sendFrame?.({ kind: "STROKE_CLEAR" });
    },
  });

  // Defensive: if currentTurn somehow isn't present, render a loading
  // placeholder rather than crash. The PHASE_CHANGED → drawing observer
  // pushes a fresh snapshot before the audience sees drawing phase, so
  // this branch shouldn't fire in production.
  if (!turn) {
    return (
      <div class="drawing-phase-shell" data-testid="drawing-phase-shell" role="status">
        <p>Loading drawing…</p>
      </div>
    );
  }

  return (
    <div
      class={`drawing-phase-shell ${PAGE_TEAR_IN_CLASS}`}
      data-testid="drawing-phase-shell"
      data-is-drawer={isDrawer ? "true" : "false"}
      data-is-spectator={isSpectator ? "true" : "false"}
      data-is-audience-correct={isAudienceCorrect ? "true" : "false"}
    >
      {/* LEFT column: V4.2.T9 players panel. The vertical <PlayersPanel> lists
          every player (drawer gets a left accent bar, "me" gets the new-palette
          highlight, each row shows its running score) and anchors the
          persistent in-game invite chip at the bottom. */}
      <aside class="drawing-phase-col drawing-phase-col--left" data-testid="drawing-phase-col-left">
        <PlayersPanel
          players={players}
          drawerId={drawerId}
          myId={playerId}
          scores={scores}
          roomId={roomId}
        />
      </aside>

      {/* CENTER column: the dominant canvas + its top band (timer · word) and
          the tool/guess row below. All existing drawing-phase behavior lives
          here unchanged. */}
      <div
        class="drawing-phase-col drawing-phase-col--center"
        data-testid="drawing-phase-col-center"
      >
        <div class="drawing-phase-header">
          <span class="drawing-phase-round" data-testid="drawing-phase-round">
            {headerLabel(
              game.currentTurnIndex,
              game.turnOrder.length,
              game.currentRound,
              game.round,
            )}
          </span>
          <DrawingPhaseTimer
            secondsLeft={secondsLeft}
            totalSeconds={phaseTotalSeconds}
            paused={!!game.drawerDisconnect}
          />
        </div>
        <div class="drawing-phase-meta">
          <span class="drawing-phase-drawer" data-testid="drawing-phase-drawer">
            Drawer: <strong>{drawerName}</strong>
          </span>
          {/* V4.2.T8 (note 5c): small muted "DRAW THIS" eyebrow above the BIG
              BOLD word. The word container keeps its testid; the skeleton (and
              its multi-word breaks from T4) renders unchanged inside it. */}
          <span class="drawing-phase-word-eyebrow" data-testid="drawing-phase-word-eyebrow">
            {/* Audit 2026-06-11: the audience was being told to draw. */}
            {isDrawer ? "Draw this" : "Guess this"}
          </span>
          <span class="drawing-phase-word" data-testid="drawing-phase-word">
            <WordSkeleton
              word={turn.word}
              wordLength={turn.wordLength}
              wordPattern={turn.wordPattern}
              revealedIndices={isSpectator ? [] : turn.revealedIndices}
              revealedLetters={isSpectator ? [] : turn.revealedLetters}
              showFullWord={showFullWord}
            />
          </span>
        </div>

        <div
          class="canvas-wrapper drawing-phase-canvas-wrapper"
          ref={canvasWrapperRef}
          style={{ cursor: isDrawer ? "none" : "default" }}
        >
          <DrawingCanvas
            readOnly={!isDrawer}
            {...(isDrawer && sendBinary ? { sendBinary } : {})}
            {...(isDrawer && sendFrame
              ? {
                  sendFill: (op) =>
                    sendFrame({
                      kind: "STROKE_FILL",
                      strokeId: op.strokeId,
                      point: op.point,
                      color: op.color,
                    }),
                }
              : {})}
          />
          {isDrawer && <CursorOverlay targetRef={canvasWrapperRef} />}
        </div>

        <div class="drawing-phase-tools" data-testid="drawing-phase-tools">
          {isDrawer ? (
            <Toolbar
              fillEnabled={true}
              onUndo={onUndo}
              onClearStart={onClearStart}
              onClearEnd={onClearEnd}
              clearHolding={clearHolding}
            />
          ) : isSpectator ? (
            <p class="drawing-phase-spectator-note" data-testid="drawing-phase-spectator-note">
              {/* The mascot watches alongside the spectator (PRD §5.5). */}
              <InkCat pose="peek" size={30} />
              Watching — you&apos;ll join the game next round.
            </p>
          ) : null}
        </div>
      </div>

      {/* RIGHT column, V4.2.T10: the public wrong-guess feed (D2) above the
          audience guess input. Everyone in the room (drawer / audience /
          spectator) sees the feed; only audience members get the input slot
          beneath it (the slot is null for drawer + spectator). */}
      <aside
        class="drawing-phase-col drawing-phase-col--right"
        data-testid="drawing-phase-col-right"
      >
        <GuessChatFeed
          chatLog={game.chatLog}
          {...(isDrawer
            ? { emptyHint: "Guesses appear here as they come in." }
            : isSpectator
              ? { emptyHint: "Watch the guesses roll in." }
              : {})}
        />
        {!isDrawer && !isSpectator && guessInputSlot ? (
          <div class="drawing-phase-guess-slot" data-testid="drawing-phase-guess-slot">
            {guessInputSlot}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
